import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { CheckCircle2, XCircle, RotateCcw } from "lucide-react";
import { useLang } from "../lib/i18n";

export default function Jeux() {
  const { t, lang } = useLang();
  const [quiz, setQuiz] = useState([]);
  const [idx, setIdx] = useState(0);
  const [selected, setSelected] = useState(null);
  const [revealed, setRevealed] = useState(false);
  const [score, setScore] = useState(0);

  useEffect(() => {
    api.get("/quiz").then((r) => setQuiz(r.data));
  }, [lang]);

  if (quiz.length === 0) return <div className="text-[#8A8D91]">{t("quiz.loading")}</div>;

  const q = quiz[idx];
  const done = idx >= quiz.length;

  const choose = (i) => {
    if (revealed) return;
    setSelected(i);
    setRevealed(true);
    if (i === q.correct) setScore((s) => s + 1);
  };

  const next = () => { setSelected(null); setRevealed(false); setIdx((i) => i + 1); };
  const restart = () => { setIdx(0); setSelected(null); setRevealed(false); setScore(0); };

  if (done) {
    const pct = Math.round((score / quiz.length) * 100);
    return (
      <div className="space-y-6" data-testid="quiz-result">
        <header>
          <h1 className="font-heading text-4xl md:text-5xl text-[#3D405B]">{t("quiz.done.title")}</h1>
          <p className="text-[#8A8D91] mt-2">{t("quiz.done.subtitle")}</p>
        </header>
        <div className="bg-white border border-[#E8E6E1] rounded-3xl p-8 md:p-12 text-center shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
          <div className="font-heading text-6xl md:text-7xl text-[#E07A5F]">{score}/{quiz.length}</div>
          <p className="mt-3 text-[#3D405B]">
            {pct >= 80 ? t("quiz.result.high") : pct >= 50 ? t("quiz.result.mid") : t("quiz.result.low")}
          </p>
          <button
            onClick={restart}
            data-testid="quiz-restart"
            className="mt-6 bg-[#E07A5F] text-white rounded-full px-6 py-3 font-medium hover:bg-[#D56D52] transition-colors inline-flex items-center gap-2"
          >
            <RotateCcw className="w-4 h-4" /> {t("quiz.restart")}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="jeux-page">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-heading text-4xl md:text-5xl text-[#3D405B]">{t("quiz.title")}</h1>
          <p className="text-[#8A8D91] mt-2 max-w-2xl">{t("quiz.subtitle")}</p>
        </div>
        <div className="text-sm text-[#3D405B] bg-white border border-[#E8E6E1] rounded-full px-4 py-2" data-testid="quiz-progress">
          {t("quiz.progress")} {idx + 1} {t("quiz.progress.of")} {quiz.length} · {t("quiz.score")} {score}
        </div>
      </header>

      <article className="bg-white border border-[#E8E6E1] rounded-3xl p-6 md:p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)]" data-testid={`question-${q.id}`}>
        <p className="font-heading text-2xl md:text-3xl text-[#3D405B] leading-snug">{q.scenario}</p>

        <div className="mt-6 space-y-3">
          {q.options.map((opt, i) => {
            const isCorrect = i === q.correct;
            const isSelected = i === selected;
            let cls = "border-[#E8E6E1] bg-white hover:border-[#3D405B]";
            if (revealed) {
              if (isCorrect) cls = "border-[#81B29A] bg-[#81B29A]/10";
              else if (isSelected) cls = "border-[#E07A5F] bg-[#E07A5F]/10";
              else cls = "border-[#E8E6E1] bg-white opacity-60";
            }
            return (
              <button
                key={i}
                onClick={() => choose(i)}
                disabled={revealed}
                data-testid={`option-${i}`}
                className={`w-full text-left rounded-2xl border-2 px-4 py-3 transition-all flex items-start gap-3 ${cls}`}
              >
                <span className="flex-1 text-[#3D405B]">{opt}</span>
                {revealed && isCorrect && <CheckCircle2 className="w-5 h-5 text-[#81B29A] shrink-0 mt-0.5" />}
                {revealed && isSelected && !isCorrect && <XCircle className="w-5 h-5 text-[#E07A5F] shrink-0 mt-0.5" />}
              </button>
            );
          })}
        </div>

        {revealed && (
          <div className="mt-5 bg-[#F4F1DE] rounded-2xl p-4 md:p-5 border border-[#E8E6E1] animate-fade-in-up" data-testid="quiz-explanation">
            <div className="text-xs uppercase tracking-widest text-[#73A38B] font-semibold mb-2">{t("quiz.why")}</div>
            <p className="text-[#3D405B] leading-relaxed">{q.explanation}</p>
            <button
              onClick={next}
              data-testid="quiz-next"
              className="mt-4 bg-[#3D405B] text-white rounded-full px-5 py-2.5 font-medium hover:bg-[#2f3147] transition-colors"
            >
              {idx === quiz.length - 1 ? t("quiz.finish") : t("quiz.next")}
            </button>
          </div>
        )}
      </article>
    </div>
  );
}
